import { getErrorMessage } from '../../../api/unwrapQuery';
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { authAPI } from '../api';
import { User } from '../models';

export const AuthContext = createContext(null);

/**
 * Cookie-based auth state. No localStorage token.
 * Init verifies session via backend (cookies HttpOnly) - tries user then staff.
 */
export const useAuthState = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const initAuth = async () => {
            try {
                // Try user verify first
                try {
                    const res = await authAPI.verifyToken();
                    const userData = res?.user || res?.data?.user;
                    if (!cancelled && userData) {
                        const userModel = new User(userData);
                        setUser(userModel);
                        setIsAuthenticated(true);
                        return;
                    }
                } catch (e) {
                    // user not authenticated, try staff
                }
                // Try staff verify (admin/operator) - for admin.pki-local.dev etc
                try {
                    const res = await authAPI.verifyAdminToken();
                    const adminData = res?.admin || res?.data?.admin || res?.user || res?.data?.user;
                    if (!cancelled && adminData) {
                        const userModel = new User(adminData);
                        setUser(userModel);
                        setIsAuthenticated(true);
                        return;
                    }
                } catch (e) {
                    // not staff either
                }
                if (!cancelled) {
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } catch {
                if (!cancelled) {
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        initAuth();
        return () => { cancelled = true; };
    }, []);

    const login = useCallback(async (maybeToken, maybeUser) => {
        try {
            // BC: called as login(token, user) or login(user) or login(admin, admin)
            let userData = maybeUser || maybeToken;
            // If only token string passed, ignore it – user will be fetched via verify
            if (typeof userData === 'string') {
                // Try user verify, then staff
                try {
                    const res = await authAPI.verifyToken();
                    userData = res?.user || res?.data?.user || null;
                } catch {}
                if (!userData) {
                    try {
                        const res = await authAPI.verifyAdminToken();
                        userData = res?.admin || res?.data?.admin || null;
                    } catch {}
                }
            }
            if (!userData) {
                try {
                    const res = await authAPI.verifyToken();
                    userData = res?.user || res?.data?.user || userData;
                } catch {}
                if (!userData) {
                    try {
                        const res = await authAPI.verifyAdminToken();
                        userData = res?.admin || res?.data?.admin || userData;
                    } catch {}
                }
            }
            if (userData) {
                const userModel = new User(userData);
                setUser(userModel);
                setIsAuthenticated(true);
                return { success: true, user: userModel };
            }
            setIsAuthenticated(false);
            return { success: false, message: 'خطا در ورود' };
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Login error:', error);
            return { success: false, message: getErrorMessage(error, 'خطا در ورود') };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            // Try staff logout first, then user
            try { await authAPI.adminLogout(); } catch {}
            try { await authAPI.logout(); } catch {}
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Logout API error:', error);
        } finally {
            setUser(null);
            setIsAuthenticated(false);
        }
    }, []);

    const updateUser = useCallback((userData) => {
        try {
            const updatedUser = new User({ ...user?.toJSON(), ...userData });
            setUser(updatedUser);
            return { success: true, user: updatedUser };
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Update user error:', error);
            return { success: false, message: getErrorMessage(error, 'خطا در ورود') };
        }
    }, [user]);

    const refreshUser = useCallback(async () => {
        try {
            let userData = null;
            try {
                const res = await authAPI.verifyToken();
                userData = res?.user || res?.data?.user;
            } catch {}
            if (!userData) {
                try {
                    const res = await authAPI.verifyAdminToken();
                    userData = res?.admin || res?.data?.admin;
                } catch {}
            }
            if (userData) {
                const userModel = new User(userData);
                setUser(userModel);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Refresh user error:', error);
        }
    }, []);

    return {
        user,
        loading,
        isAuthenticated,
        login,
        logout,
        updateUser,
        refreshUser,
    };
};

export const useAuth = () => {
    const shared = useContext(AuthContext);
    const local = useAuthState();
    return shared || local;
};

export const AuthProvider = ({ children }) => {
    const value = useAuthState();
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default useAuth;
